<template>
    <div class="grid">
        <div class="col-12">
            <div class="card">
                <Toast />
                <div class="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-3">
                    <h5 class="m-0">Kardex de Productos</h5>
                    <Button
                        label="Reporte General"
                        icon="pi pi-file-export"
                        class="p-button p-button-primary report-general-button"
                        @click="showReportDialog = true"
                    />
                </div>
                <div class="table-header-inputs-container">
                        <span class="product-autocomplete-desktop">
                            <label for="product" class="filter-label">Producto:</label>
                            <AutoComplete
                                v-model="selectedProductObj"
                                :suggestions="productSuggestions"
                                @complete="onProductSearch"
                                placeholder="Buscar producto..."
                                id="product-autocomplete"
                                inputStyle="width: 100%;"
                                field="name"
                                appendTo="self"
                            >

                                <template #item="{ item }">
                                    <div class="autocomplete-item-custom">
                                        <div class="autocomplete-item-title">{{ item.code }}</div>
                                        <div class="autocomplete-item-desc">{{ item.name }}</div>
                                    </div>
                                </template>

                            </AutoComplete>
                        </span>
                        <span class="filter-item">
                            <label for="startDate" class="filter-label">Fecha inicial:</label>
                            <Calendar
                                :showIcon="true"
                                :showButtonBar="true"
                                id="fechainicio"
                                v-model="initialDate"
                                :showTime="true"
                                hourFormat="24"
                            />
                        </span>
                        <span class="filter-item">
                            <label for="endDate" class="filter-label">Fecha final:</label>
                            <Calendar
                                :showIcon="true"
                                :showButtonBar="true"
                                id="fechafinal"
                                v-model="finalDate"
                                :showTime="true"
                                hourFormat="24"
                                placeholder="Fecha final"
                            />
                        </span>
                        <span class="filter-item" style="align-self: flex-end;">
                            <Button
                                label="Buscar"
                                class="p-button-success"
                                @click="onSearchKardex"
                                :loading="loadingSearch"
                            />
                        </span>
                    </div>
                <div v-if="selectedProductHasNoStock" class="alert-no-stock">
                    El producto/servicio no registra inventario, por lo que no se calcula el saldo.
                </div>
                
                <!-- Resumen de Kardex -->
                <div v-if="filteredKardex.length > 0" class="kardex-summary">
                    <div class="summary-card">
                        <div class="summary-icon" style="background: #ef4444;">
                            <i class="pi pi-arrow-down"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Total Entrada</div>
                            <div class="summary-value">{{ formatDecimals(kardexSummary.totalIn) }}</div>
                        </div>
                    </div>
                    <div class="summary-card">
                        <div class="summary-icon" style="background: #22c55e;">
                            <i class="pi pi-arrow-up"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Total Salida</div>
                            <div class="summary-value">{{ formatDecimals(kardexSummary.totalOut) }}</div>
                        </div>
                    </div>
                    <div class="summary-card">
                        <div class="summary-icon" style="background: #3b82f6;">
                            <i class="pi pi-shopping-cart"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Total Compra</div>
                            <div class="summary-value">${{ roundToTwo(kardexSummary.totalPurchase) }}</div>
                            <div v-if="selectedProductObj && selectedProductObj.hasTax" class="summary-iva">
                                c/IVA: ${{ roundToTwo(kardexSummary.totalPurchaseIva) }}
                            </div>
                        </div>
                    </div>
                    <div class="summary-card">
                        <div class="summary-icon" style="background: #10b981;">
                            <i class="pi pi-dollar"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Total Venta</div>
                            <div class="summary-value">${{ roundToTwo(kardexSummary.totalSale) }}</div>
                            <div v-if="selectedProductObj && selectedProductObj.hasTax" class="summary-iva">
                                c/IVA: ${{ roundToTwo(kardexSummary.totalSaleIva) }}
                            </div>
                        </div>
                    </div>
                    <!-- Kardex híbrido: costo de ventas (COGS) y utilidad directa -->
                    <div class="summary-card">
                        <div class="summary-icon" style="background: #f59e0b;">
                            <i class="pi pi-wallet"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Costo de Venta (COGS)</div>
                            <div class="summary-value">${{ roundToTwo(kardexSummary.totalCogs) }}</div>
                        </div>
                    </div>
                    <div class="summary-card">
                        <div class="summary-icon" :style="{ background: (kardexSummary.totalMargin < 0 ? '#ef4444' : '#8b5cf6') }">
                            <i class="pi pi-chart-line"></i>
                        </div>
                        <div class="summary-content">
                            <div class="summary-label">Utilidad (Margen)</div>
                            <div class="summary-value">${{ roundToTwo(kardexSummary.totalMargin) }}</div>
                        </div>
                    </div>
                </div>
                
                <DataTable
                    :value="filteredKardex"
                    :paginator="true"
                    :rows="20"
                    :rowsPerPageOptions="[20, 35, 50]"
                    responsiveLayout="scroll"
                    :scrollable="true"
                    scrollHeight="calc(100vh - 24rem)"
                    class="p-datatable-scrollable-table"
                >
                    <Column field="date" header="Fecha" :style="colStyle.date" :headerStyle="colStyle.date">
                        <template #body="slotProps">
                            {{ formatDate(slotProps.data.date) }}
                        </template>
                    </Column>
                    <Column field="type" header="Tipo" :style="colStyle.type" :headerStyle="colStyle.type">
                        <template #body="slotProps">
                            <span :class="['badge', getBadgeClass(slotProps.data.type)]">
                                {{ slotProps.data.type }}
                            </span>
                        </template>
                    </Column>
                    <Column field="document" header="Referencia" :style="colStyle.reference" :headerStyle="colStyle.reference">
                        <template #body="slotProps">
                            <div v-if="slotProps.data.type !== 'Saldo inicial'" class="reference-container">
                                <a
                                    href="#"
                                    @click.prevent="goToDocument(slotProps.data)"
                                    class="document-link"
                                    v-if="(slotProps.data.type === 'Venta' && slotProps.data.saleId) || (slotProps.data.type === 'Compra' && slotProps.data.purchaseId)"
                                >
                                    {{ slotProps.data.document }}
                                </a>
                                <div v-if="slotProps.data.adjustment" class="reference-adjustment">
                                    <span class="badge-adjustment">{{ slotProps.data.adjustment.sourceLabel }}<span v-if="slotProps.data.adjustment.inventoryCountId"> #{{ slotProps.data.adjustment.inventoryCountId }}</span></span>
                                    {{ slotProps.data.adjustment.creator }} · {{ slotProps.data.adjustment.reasonLabel }}
                                    <span v-if="slotProps.data.adjustment.needsReview" class="adjustment-review" title="El stock previo era negativo: ¿falta registrar una compra?">Revisar</span>
                                </div>
                                <div v-if="slotProps.data.reference" class="reference-secondary">
                                    {{ slotProps.data.reference }}
                                </div>
                            </div>
                        </template>
                    </Column>
                    <Column field="in" header="Entrada" :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-red">{{ formatDecimals(slotProps.data.in) }}</span>
                        </template>
                    </Column>
                    <Column field="out" header="Salida" :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-green">{{ formatDecimals(slotProps.data.out) }}</span>
                        </template>
                    </Column>
                    <Column field="balance" header="Saldo" :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-bold">{{ formatDecimals(slotProps.data.balance) }}</span>
                        </template>
                    </Column>
                    <!-- Saldo valorizado: valor del inventario a costo en cada
                         punto (= Σcompras − ΣCOGS). El "*" marca movimientos sin
                         costo conocido que el valor no pudo considerar. -->
                    <Column field="valuedBalance" header="Saldo Valor." :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-bold">{{ formatMoneyOrDash(slotProps.data.valuedBalance) }}</span>
                            <span v-if="slotProps.data.valuedUncertain" style="color:#f59e0b;" title="Movimiento sin costo conocido; el saldo valorizado no lo considera">&nbsp;*</span>
                        </template>
                    </Column>
                    <!-- Kardex híbrido: costo (COGS) + venta + margen. La Venta
                         y el Margen solo aplican a ventas reales; en consumos
                         por composición / ajustes se muestra "—". -->
                    <Column field="unitCost" header="Costo U." :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-bold">{{ formatMoneyOrDash(slotProps.data.unitCost) }}</span>
                        </template>
                    </Column>
                    <Column field="unitPrice" header="Venta U." :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-bold">{{ saleUnit(slotProps.data) }}</span>
                        </template>
                    </Column>
                    <Column field="totalPrice" header="Total Venta" :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span class="text-bold">{{ formatMoneyOrDash(slotProps.data.totalPrice) }}</span>
                        </template>
                    </Column>
                    <Column field="margin" header="Margen" :style="colStyle.num" :headerStyle="colStyle.num">
                        <template #body="slotProps">
                            <span :class="marginClass(slotProps.data.margin)">{{ formatMoneyOrDash(slotProps.data.margin) }}</span>
                        </template>
                    </Column>
                    <template #empty>
                        <label style="display: block; width: 100%; text-align: center; padding: 0.5rem; color: #000000;">
                          No hay registros para este producto.
                        </label>
                    </template>
                </DataTable>
            </div>
        </div>
        
        <!-- Dialog para Reporte General -->
        <Dialog
            v-model:visible="showReportDialog"
            :style="{width: '450px'}"
            header="Reporte General de Kardex"
            :modal="true"
            class="p-fluid"
        >
            <div class="field">
                <label for="report-start-date" class="font-semibold">Fecha Inicial</label>
                <Calendar
                    id="report-start-date"
                    v-model="reportStartDate"
                    :showIcon="true"
                    :showButtonBar="true"
                    :showTime="true"
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                />
            </div>
            <div class="field">
                <label for="report-end-date" class="font-semibold">Fecha Final</label>
                <Calendar
                    id="report-end-date"
                    v-model="reportEndDate"
                    :showIcon="true"
                    :showButtonBar="true"
                    :showTime="true"
                    hourFormat="24"
                    dateFormat="dd/mm/yy"
                />
            </div>
            <div class="field">
                <label for="report-email" class="font-semibold">Email</label>
                <InputText
                    id="report-email"
                    v-model="reportEmail"
                    type="email"
                    placeholder="correo@ejemplo.com"
                    :class="{'p-invalid': reportEmailError}"
                />
                <small v-if="reportEmailError" class="p-error">{{ reportEmailError }}</small>
            </div>
            <div class="field">
                <label class="font-semibold">Tipo de Reporte</label>
                <div class="report-type-options">
                    <div v-for="option in reportTypeOptions" :key="option.value" class="report-type-option">
                        <RadioButton
                            :inputId="'report-type-' + option.value"
                            v-model="reportType"
                            :value="option.value"
                        />
                        <label :for="'report-type-' + option.value" class="report-type-label">{{ option.label }}</label>
                    </div>
                </div>
            </div>
            <template #footer>
                <Button
                    label="Cancelar"
                    icon="pi pi-times"
                    class="p-button-text"
                    @click="closeReportDialog"
                />
                <Button
                    label="Generar Reporte"
                    icon="pi pi-check"
                    class="p-button-primary"
                    @click="generateReport"
                    :loading="loadingReport"
                />
            </template>
        </Dialog>
    </div>
</template>

<script>
import Big from 'big.js';
import httpRequest from '../../service/httpRequest';
import { startOfLocalDay, endOfLocalDay } from '../../service/dates';
import { kardexDefaultRange } from './kardexDefaults';
import Product from '../../models/product';

export default {
    data() {
        return {
            // Anchos de columna del kardex (modo scrollable = header y body
            // independientes → mismo estilo en :style y :headerStyle). Las
            // numéricas se acotan con max-width; Referencia solo tiene min-width
            // (sin tope) para absorber el espacio sobrante en pantallas grandes.
            // En pantallas chicas los min-width fuerzan el scroll horizontal.
            colStyle: {
                date: { minWidth: '8rem', maxWidth: '11rem' },
                type: { minWidth: '6rem', maxWidth: '8rem' },
                reference: { minWidth: '20rem' },
                num: { minWidth: '5.5rem', maxWidth: '7.5rem' },
            },
            selectedProductObj: null,
            // Desde el día 1 del mes (H68): con la hora actual no salía lo de hoy.
            ...kardexDefaultRange(),
            products: [],
            kardex: [],
            loadingSearch: false,
            productSuggestions: [],
            // Reporte General
            showReportDialog: false,
            reportStartDate: new Date(),
            reportEndDate: null,
            reportEmail: '',
            reportEmailError: '',
            loadingReport: false,
            reportType: 'detallado',
            reportTypeOptions: [
                { label: 'Detallado', value: 'detallado' },
                { label: 'Consolidado', value: 'consolidado' }
            ]
        };
    },
    computed: {
        selectedProduct() {
            return this.selectedProductObj ? this.selectedProductObj.id : null;
        },
        filteredKardex() {
            let filtered = this.kardex.filter(entry => entry.productId === this.selectedProduct);
            // Se comparan instantes, no textos: `entry.date` viene en UTC y la fecha elegida es
            // local; comparando textos, lo de la noche caía fuera del día (H64).
            if (this.initialDate) {
                const start = startOfLocalDay(this.initialDate);
                filtered = filtered.filter(entry => new Date(entry.date) >= start);
            }
            if (this.finalDate) {
                const end = endOfLocalDay(this.finalDate);
                filtered = filtered.filter(entry => new Date(entry.date) <= end);
            }
            return filtered;
        },
        selectedProductHasNoStock() {
            const prod = this.selectedProductObj;
            return prod && prod.hasStock === false;
        },
        kardexSummary() {
            const summary = {
                totalIn: 0,
                totalOut: 0,
                totalPurchase: 0,
                totalSale: 0,
                totalCogs: 0,    // Costo de ventas directas (Σ costo de lo vendido)
                totalMargin: 0   // Utilidad = Σ margen de ventas directas
            };
            
            if (!this.filteredKardex || this.filteredKardex.length === 0) {
                return summary;
            }
            
            // Recopilar ajustes de venta y devolución para emparejar
            const saleAdjustments = [];
            const returnAdjustments = [];
            
            this.filteredKardex.forEach(entry => {
                try {
                    // Sumar entradas y salidas
                    if (entry.in) {
                        summary.totalIn = new Big(summary.totalIn).plus(entry.in).toNumber();
                    }
                    if (entry.out) {
                        summary.totalOut = new Big(summary.totalOut).plus(entry.out).toNumber();
                    }
                    
                    // Calcular totales de compra y venta
                    const unitPrice = new Big(entry.unitPrice || 0);
                    const discount = new Big(entry.discount || 0);
                    const finalPrice = unitPrice.times(discount.times(-1).plus(1));
                    
                    if (entry.type === 'Compra' && entry.in) {
                        const purchaseAmount = finalPrice.times(entry.in);
                        summary.totalPurchase = new Big(summary.totalPurchase).plus(purchaseAmount).toNumber();
                    }
                    
                    if (entry.type === 'Venta' && entry.out) {
                        const saleAmount = finalPrice.times(entry.out);
                        summary.totalSale = new Big(summary.totalSale).plus(saleAmount).toNumber();
                    }

                    // Kardex híbrido: COGS y utilidad de todo lo que SE VENDIÓ,
                    // incluyendo consumos por composición. El backend marca esas
                    // filas con `margin != null` (ventas directas + composición);
                    // los ajustes manuales y las compras tienen margin = null, así
                    // que quedan fuera. margin != null garantiza totalCost != null.
                    if (entry.margin !== null && entry.margin !== undefined) {
                        summary.totalCogs = new Big(summary.totalCogs).plus(entry.totalCost).toNumber();
                        summary.totalMargin = new Big(summary.totalMargin).plus(entry.margin).toNumber();
                    }
                    
                    // Ajustes huérfanos de venta/devolución por composición
                    if (entry.type !== 'Venta' && entry.type !== 'Compra') {
                        const doc = entry.document || '';
                        let qty = 0;
                        if (entry.out !== null && entry.out !== undefined && Number(entry.out) !== 0) {
                            qty = entry.out;
                        } else if (entry.in !== null && entry.in !== undefined && Number(entry.in) !== 0) {
                            qty = entry.in;
                        }
                        const totalPrice = finalPrice.times(qty).toNumber();
                        const key = this.extractAdjustmentKey(doc);
                        
                        if (!entry.saleId && doc.startsWith('Venta ') && totalPrice > 0) {
                            saleAdjustments.push({ totalPrice, key, qtyOut: entry.out || 0 });
                        } else if (doc.startsWith('Devolución ') && key) {
                            returnAdjustments.push({ totalPrice, key, qtyIn: entry.in || 0 });
                        }
                    }
                } catch (e) {
                    console.error('Error calculando totales:', e);
                }
            });
            
            // Emparejar devoluciones con ajustes de venta y anularlos
            const usedReturns = new Set();
            saleAdjustments.forEach(sale => {
                if (!sale.key) return;
                const retIdx = returnAdjustments.findIndex((ret, idx) =>
                    !usedReturns.has(idx) && ret.key === sale.key
                );
                if (retIdx !== -1) {
                    usedReturns.add(retIdx);
                    sale.cancelled = true;
                    // Anular cantidades de entrada y salida del par
                    summary.totalOut = new Big(summary.totalOut).minus(sale.qtyOut).toNumber();
                    summary.totalIn = new Big(summary.totalIn).minus(returnAdjustments[retIdx].qtyIn).toNumber();
                }
            });
            
            // Sumar ajustes de venta no anulados al total de venta
            saleAdjustments.forEach(sale => {
                if (!sale.cancelled) {
                    summary.totalSale = new Big(summary.totalSale).plus(sale.totalPrice).toNumber();
                }
            });
            
            // Calcular totales con IVA
            const business = this.$store.getters.business;
            const taxPct = parseFloat((business && business.taxPercentage) || 0);
            const taxMultiplier = new Big(1).plus(new Big(taxPct).div(100));
            summary.totalPurchaseIva = new Big(summary.totalPurchase).times(taxMultiplier).toNumber();
            summary.totalSaleIva = new Big(summary.totalSale).times(taxMultiplier).toNumber();
            
            return summary;
        }
    },
    mounted() {
        const user = this.$store.getters.getUser;
        if (user && user.email) {
            this.reportEmail = user.email;
        }
    },
    methods: {
        formatDate(dateStr) {
            const date = new Date(dateStr);
            const dd = String(date.getDate()).padStart(2, '0');
            const mm = String(date.getMonth() + 1).padStart(2, '0');
            const yyyy = date.getFullYear();
            const hh = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            return `${hh}:${min} - ${dd}/${mm}/${yyyy}`;
        },
        async onProductSearch(event) {
            const query = event.query;
            if (!query || query.length < 2) {
                this.productSuggestions = [];
                return;
            }
            const response = await httpRequest.getProducts(query, null, true);
            if (response.status === 200) {
                this.productSuggestions = response.data.map(product => new Product(product));
            } else {
                this.productSuggestions = [];
            }
        },
        async onSearchKardex() {
            // Verificación de producto seleccionado
            if (!this.selectedProductObj) {
                this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'Seleccione un producto antes de buscar.', life: 3000 });
                return;
            }
            // Verificación de fecha inicial
            const now = new Date();
            let initialDateObj = this.initialDate instanceof Date ? this.initialDate : new Date(this.initialDate);
            if (initialDateObj > new Date(now.getTime() + 24 * 60 * 60 * 1000)) {
                this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'La fecha inicial no puede ser mayor a la fecha actual.', life: 3000 });
                return;
            }
            // Verificación de fecha final
            if (this.finalDate) {
                let finalDateObj = this.finalDate instanceof Date ? this.finalDate : new Date(this.finalDate);
                if (finalDateObj < initialDateObj) {
                    this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'La fecha final no puede ser menor que la fecha inicial.', life: 3000 });
                    return;
                }
            }
            this.loadingSearch = true;
            // Instantes de borde del día local: la fecha final incluye su día completo (H64).
            const initialDateStr = this.initialDate ? startOfLocalDay(this.initialDate).toISOString() : null;
            const finalDateStr = this.finalDate ? endOfLocalDay(this.finalDate).toISOString() : null;
            const response = await httpRequest.getProductKardex(this.selectedProduct, initialDateStr, finalDateStr);
            this.loadingSearch = false;
            if (response.status === 200) {
                this.kardex = response.data;
            } else {
                this.kardex = [];
            }
        },
        goToDocument(entry) {
            let url = '';
            if (entry.type === 'Venta' && entry.saleId) {
                url = `/dashboard/venta/form?id=${entry.saleId}`;
            } else if (entry.type === 'Compra' && entry.purchaseId) {
                url = `/dashboard/compra/form?id=${entry.purchaseId}`;
            }
            if (url) {
                window.open(url, '_blank');
            }
        },
        roundToTwo(value) {
            try {
                return new Big(value || 0).round(2).toFixed(2);
            } catch (e) {
                return '0.00';
            }
        },
        formatDecimals(number_) {
            if (!number_) return '0.00';
            const number = new Big(number_);
            const str = number.toString();
            if (!str.includes('.')) return number.toFixed(2);
            const decimals = str.split('.')[1];
            if (decimals.length > 2 && !/^0{2}$/.test(decimals.slice(-2))) {
                return parseFloat(number.toFixed(4)).toString();
            }
            return number.toFixed(2);
        },
        formatUnitCost(unitPrice, discount) {
            if (unitPrice === null || unitPrice === undefined) return '—';
            try {
                const price = new Big(unitPrice || 0);
                const disc = new Big(discount || 0);
                const result = price.times(disc.times(-1).plus(1)).round(2);
                return this.formatDecimals(result);
            } catch (e) {
                return '0.0000';
            }
        },
        // Formatea un valor monetario del kardex híbrido (costo/total/margen).
        // Devuelve "—" cuando el backend manda null (p.ej. venta/margen en un
        // consumo por composición, que no tiene venta directa).
        formatMoneyOrDash(value) {
            if (value === null || value === undefined || value === '') return '—';
            try {
                return '$' + new Big(value).round(2).toFixed(2);
            } catch (e) {
                return '—';
            }
        },
        // "Venta U." solo aplica a SALIDAS con valor de venta: ventas directas
        // y consumos por composición (el backend ya manda el valor atribuido, o
        // null en ajustes manuales). Compras/entradas (out=0) van "—".
        saleUnit(row) {
            if (Number(row.out) > 0 && row.unitPrice !== null && row.unitPrice !== undefined) {
                return this.formatUnitCost(row.unitPrice, row.discount);
            }
            return '—';
        },
        // Color del margen: verde si es ganancia, rojo si es pérdida.
        marginClass(value) {
            if (value === null || value === undefined || value === '') return 'text-bold';
            try {
                return new Big(value).lt(0) ? 'text-red' : 'text-green';
            } catch (e) {
                return 'text-bold';
            }
        },
        formatTotalPrice(entry) {
            try {
                const price = new Big(entry.unitPrice || 0);
                const disc = new Big(entry.discount || 0);
                const unitCost = price.times(disc.times(-1).plus(1));
                
                // Determinar la cantidad: usar in para entradas, out para salidas
                let qty = 0;
                if (entry.in !== null && entry.in !== undefined && Number(entry.in) !== 0) {
                    qty = entry.in;
                } else if (entry.out !== null && entry.out !== undefined && Number(entry.out) !== 0) {
                    qty = entry.out;
                }
                
                const quantity = new Big(qty);
                const total = unitCost.times(quantity).round(2);
                return total.toFixed(2);
            } catch (e) {
                console.error('Error en formatTotalPrice:', e, 'Entry:', JSON.stringify(entry));
                return '0.00';
            }
        },
        extractAdjustmentKey(doc) {
            // Extraer composición y número para emparejar ventas con devoluciones
            // Venta: "Venta - Composición: X → Y (×N) | Venta #870"
            // Devolución: "Devolución - Composición: X → Y (×N) | Ajuste de venta #870"
            const compMatch = doc.match(/Composición:\s*(.+?)\s*\|/);
            const numMatch = doc.match(/#(\d+)\s*$/);
            if (compMatch && numMatch) {
                return compMatch[1].trim() + '|' + numMatch[1];
            }
            return null;
        },
        getBadgeClass(type) {
            if (!type) return 'badge-default';
            
            // Normalizar el tipo para comparación
            const typeNormalized = type.toLowerCase().trim();
            
            // Mapeo de tipos
            if (typeNormalized.includes('venta') || typeNormalized === 'sale') {
                return 'badge-sale';
            }
            if (typeNormalized.includes('compra') || typeNormalized === 'purchase') {
                return 'badge-purchase';
            }
            // Detectar ajustes positivos (con (+) o +)
            if (typeNormalized.includes('ajuste') && (typeNormalized.includes('(+)') || typeNormalized.includes('+'))) {
                return 'badge-adjustment-plus';
            }
            // Detectar ajustes negativos (con (-) o -)
            if (typeNormalized.includes('ajuste') && (typeNormalized.includes('(-)') || typeNormalized.includes('-'))) {
                return 'badge-adjustment-minus';
            }
            // Otros tipos de entrada/salida
            if (typeNormalized.includes('entrada') || typeNormalized === 'in') {
                return 'badge-adjustment-plus';
            }
            if (typeNormalized.includes('salida') || typeNormalized === 'out') {
                return 'badge-adjustment-minus';
            }
            
            return 'badge-default';
        },
        closeReportDialog() {
            this.showReportDialog = false;
            this.reportEmailError = '';
        },
        validateReportEmail() {
            if (!this.reportEmail) {
                this.reportEmailError = 'El email es requerido';
                return false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(this.reportEmail)) {
                this.reportEmailError = 'Email inválido';
                return false;
            }
            this.reportEmailError = '';
            return true;
        },
        async generateReport() {
            // Validar email
            if (!this.validateReportEmail()) {
                return;
            }
            
            // Validar fechas
            if (!this.reportStartDate) {
                this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'La fecha inicial es requerida', life: 3000 });
                return;
            }
            
            const now = new Date();
            let startDateObj = this.reportStartDate instanceof Date ? this.reportStartDate : new Date(this.reportStartDate);
            
            if (startDateObj > new Date(now.getTime() + 24 * 60 * 60 * 1000)) {
                this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'La fecha inicial no puede ser mayor a la fecha actual', life: 3000 });
                return;
            }
            
            if (this.reportEndDate) {
                let endDateObj = this.reportEndDate instanceof Date ? this.reportEndDate : new Date(this.reportEndDate);
                if (endDateObj < startDateObj) {
                    this.$toast.add({ severity: 'warn', summary: 'Advertencia', detail: 'La fecha final no puede ser menor que la fecha inicial', life: 3000 });
                    return;
                }
            }
            
            // Preparar datos
            this.loadingReport = true;
            // Del inicio del primer día al final del último, en hora local (H64).
            const startDate = startOfLocalDay(this.reportStartDate).toISOString();
            const endDate = endOfLocalDay(this.reportEndDate || new Date()).toISOString();
            
            // Enviar solicitud
            const response = await httpRequest.generateProductKardexReport(startDate, endDate, this.reportEmail, this.reportType);
            this.loadingReport = false;
            
            if (response.status === 200) {
                this.$toast.add({ 
                    severity: 'success', 
                    summary: 'Éxito', 
                    detail: `Reporte generado exitosamente. Se enviará a ${this.reportEmail}`, 
                    life: 5000 
                });
                this.closeReportDialog();
                // Limpiar campos
                this.reportStartDate = new Date();
                this.reportEndDate = null;
                this.reportEmail = '';
                this.reportType = 'detallado';
            } else {
                this.$toast.add({ 
                    severity: 'error', 
                    summary: 'Error', 
                    detail: response.message || 'Error al generar el reporte', 
                    life: 5000 
                });
            }
        }
    }
};
</script>

<style scoped>
    /* Estilos para mostrar el texto en dos líneas y evitar desbordes */
    .autocomplete-item-custom {
        display: flex;
        flex-direction: column;
        font-size: 0.92rem;
        padding: 2px 0;
        max-width: 100%;
        white-space: normal;
    }
    .autocomplete-item-title {
        font-weight: 600;
        color: #2d3a4b;
        font-size: 0.95rem;
        margin-bottom: 2px;
        word-break: break-all;
    }
    .autocomplete-item-desc {
        font-size: 0.88rem;
        color: #444;
        word-break: break-word;
        line-height: 1.2;
        display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: normal;
        max-height: 2.6em;
    }
    .table-header-inputs-container {
        display: flex;
        align-items: flex-end;
        gap: 18px;
        background: #f6f8fa;
        border-radius: 10px;
        padding: 18px 18px 10px 18px;
        margin-bottom: 18px;
        flex-wrap: wrap;
    }
    .filter-item {
        display: flex;
        flex-direction: column;
        min-width: 170px;
        flex: 0 0 auto;
        margin-bottom: 0;
    }
    .product-autocomplete-desktop {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-width: 280px;
        margin-bottom: 0;
    }
    .filter-label {
        font-weight: 600;
        margin-bottom: 6px;
        color: #2d3a4b;
        font-size: 1rem;
    }
    .text-green {
        color: #22c55e;
        font-weight: 500;
    }
    .text-red {
        color: #ef4444;
        font-weight: 500;
    }
    .text-bold {
        font-weight: 700;
    }
    .badge {
        display: inline-block;
        min-width: 80px;
        text-align: center;
        padding: 2px 10px;
        border-radius: 5px;
        font-size: 0.95em;
        font-weight: 600;
        color: #fff;
        box-sizing: border-box;
    }
    .badge-sale {
        background: #22c55e;
    }
    .badge-purchase {
        background: #ef4444;
    }
    .badge-adjustment-plus {
        background: #3b82f6;
    }
    .badge-adjustment-minus {
        background: #f97316;
    }
    .badge-default {
        background: #6b7280;
    }
    .document-link {
        color: #4f8cff;
        text-decoration: underline;
        cursor: pointer;
        font-weight: 500;
    }
    .document-link:hover {
        color: #1d4ed8;
        text-decoration: underline;
    }
    .reference-container {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }
    .reference-secondary {
        font-size: 0.85rem;
        color: #6b7280;
        font-style: italic;
    }
    .alert-no-stock {
        background: #fff3cd;
        color: #856404;
        border: 1px solid #ffeeba;
        border-radius: 6px;
        padding: 0.75rem 1rem;
        margin-bottom: 1rem;
        font-size: 1rem;
        text-align: center;
    }
    
    .report-general-button {
        white-space: nowrap;
    }
    
    .summary-iva {
        font-size: 0.75rem;
        color: #9ca3af;
        font-weight: 500;
        margin-top: 2px;
    }
    
    /* Estilos del resumen de kardex */
    .kardex-summary {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 16px;
        margin-bottom: 20px;
        padding: 4px;
    }
    
    .summary-card {
        background: #ffffff;
        border: 1px solid #e5e7eb;
        border-radius: 10px;
        padding: 16px;
        display: flex;
        align-items: center;
        gap: 14px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        transition: all 0.2s ease;
    }
    
    .summary-card:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
    }
    
    .summary-icon {
        width: 48px;
        height: 48px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }
    
    .summary-icon i {
        font-size: 1.5rem;
        color: #ffffff;
    }
    
    .summary-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
        flex: 1;
    }
    
    .summary-label {
        font-size: 0.85rem;
        color: #6b7280;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }
    
    .summary-value {
        font-size: 1.5rem;
        font-weight: 700;
        color: #1f2937;
        line-height: 1;
    }
    
    /* Tablets y pantallas medianas */
    @media (max-width: 1200px) and (min-width: 961px) {
        .kardex-summary {
            grid-template-columns: repeat(2, 1fr);
        }
        
        .table-header-inputs-container {
            gap: 14px;
            padding: 16px;
        }
        
        .product-autocomplete-desktop {
            flex: 1 1 100%;
            min-width: 0;
        }
        
        .filter-item {
            flex: 0 0 calc(33.333% - 14px);
            min-width: 160px;
        }
    }
    
    @media (max-width: 960px) and (min-width: 701px) {
        .kardex-summary {
            grid-template-columns: repeat(2, 1fr);
        }
        
        .table-header-inputs-container {
            gap: 12px;
            padding: 14px;
        }
        
        .product-autocomplete-desktop {
            flex: 1 1 100%;
            min-width: 0;
        }
        
        .filter-item {
            flex: 0 0 calc(50% - 12px);
            min-width: 140px;
        }
    }
    
    /* Móviles - layout vertical */
    @media (max-width: 700px) {
        .kardex-summary {
            grid-template-columns: 1fr;
            gap: 12px;
        }
        
        .summary-card {
            padding: 14px;
        }
        
        .summary-icon {
            width: 44px;
            height: 44px;
        }
        
        .summary-icon i {
            font-size: 1.3rem;
        }
        
        .summary-value {
            font-size: 1.3rem;
        }
        
        .report-general-button {
            width: 100%;
            margin-top: 12px;
            justify-content: center;
        }
        
        .table-header-inputs-container {
            flex-direction: column;
            align-items: stretch;
            gap: 14px;
            padding: 14px 12px 12px 12px;
            margin-bottom: 14px;
        }
        
        .filter-item {
            flex: 1 1 100%;
            min-width: 0;
        }
        
        .product-autocomplete-desktop {
            flex: 1 1 100%;
            min-width: 0;
        }
        
        .filter-label {
            font-size: 0.98rem;
            margin-bottom: 4px;
        }
        
        /* Hacer el botón de búsqueda full width en móviles */
        .filter-item .p-button {
            width: 100%;
        }
    }
    
    /* Estilos para el Dialog de Reporte General */
    ::v-deep(.p-dialog .p-dialog-header) {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
    }
    
    ::v-deep(.p-dialog .p-dialog-header .p-dialog-title) {
        color: white;
        font-weight: 600;
    }
    
    ::v-deep(.p-dialog .p-dialog-header .p-dialog-header-icon) {
        color: white;
    }
    
    ::v-deep(.p-dialog .p-dialog-header .p-dialog-header-icon:hover) {
        background: rgba(255, 255, 255, 0.2);
    }
    
    .field {
        margin-bottom: 1.5rem;
    }
    
    .field label {
        display: block;
        margin-bottom: 0.5rem;
        color: #495057;
        font-weight: 600;
    }
    
    .field small.p-error {
        display: block;
        margin-top: 0.25rem;
    }
    
    .report-type-options {
        display: flex;
        gap: 24px;
        padding-top: 4px;
    }
    
    .report-type-option {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .report-type-label {
        cursor: pointer;
        margin-bottom: 0 !important;
    }
.reference-adjustment {
    font-size: 0.8rem;
    margin-top: 2px;
}
.badge-adjustment {
    display: inline-block;
    padding: 0 6px;
    margin-right: 4px;
    border-radius: 4px;
    background: var(--surface-200);
    font-weight: 600;
}
.adjustment-review {
    margin-left: 4px;
    padding: 0 6px;
    border-radius: 4px;
    background: #fff3cd;
    color: #8a6d00;
    font-weight: 600;
}
</style>